import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IMapTemplate } from '../../csv-mapper/csv-map.class';
import { Charts, ChartTypes, IBasicOpt, IChartModule, sortBySeverityColors } from '../charts.class';

@Component({
  selector: 'app-basic-linebar-chart',
  templateUrl: './basic-linebar-chart.component.html',
  styleUrls: ['./basic-linebar-chart.component.sass']
})
/**
 * This component will be the example component for future components
 * 
 * Requirements:
 * 
 * All new charts will need to extend ChartModule
 */
export class BasicLinebarChartComponent implements OnInit, OnDestroy, IChartModule {

  /** Upon creating new IChartModule, copy all the codes below - START */

  /** Array of data that comes from parent component */
  @Input() set data(_data: any[]) {
    this._data = _data
    /** Quick hack to resolve error */
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  /** Mapping will need to match whatver data is required to generate the chart */
  @Input() set mapping(_mapping: IMapTemplate) {
    this._mapping = _mapping
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  @Input() set noDataMsg(msg: string) {
    this.ui.noDataMsg = msg
  }

  /**
   * Output event emitted when chart is clicked
   * TODO: For Charts Module
   * Set the type that's standardized across all IChartModule
   */
  @Output() chartClicked = new EventEmitter()

  /** To be read by Charts Module */
  static SCHEMA = {
    name: 'Basic Line Bar Chart',
    id: ChartTypes.basicLB,
    fields: [
      {
        text: 'X-Axis',
        placeholder: 'X-Axis',
        id: 'xaxis',
        key: ''
      },
      {
        text: 'Y-Axis',
        placeholder: 'Y-Axis',
        id: 'yaxis',
        key: ''
      },
      {
        text: 'Color',
        placeholder: 'Custom Color',
        id: 'custom-color',
        key: ''
      },
      {
        text: 'ID',
        placeholder: 'Data unique ID',
        id: 'data-id',
        key: ''
      }
    ]
  }

  /** ID for uniqueness (placeholder) */
  id: string
  /** Observable to trigger changes when mapping and data changes */
  initComp$: Subject<boolean> = new Subject<boolean>()
  /** Unsubber */
  unsubber: Subject<boolean> = new Subject<boolean>()

  _mapping: IMapTemplate
  _data: any[]
  /** Setting for the chart (?) */
  @Input() _settings: {
    diffColorBars?: boolean
    hideAxisVal?: boolean
    showEmptyBars?: boolean
  } = {
      diffColorBars: false,
      hideAxisVal: false
    }

  SCHEMA: IMapTemplate = {
    name: 'Basic Line Bar Chart',
    id: ChartTypes.basicLB,
    fields: [
      {
        text: 'X-Axis',
        placeholder: 'X-Axis',
        id: 'xaxis',
        key: ''
      },
      {
        text: 'Y-Axis',
        placeholder: 'Y-Axis',
        id: 'yaxis',
        key: ''
      },
      {
        text: 'Color',
        placeholder: 'Custom Color',
        id: 'custom-color',
        key: ''
      },
      {
        text: 'ID',
        placeholder: 'Data unique ID',
        id: 'data-id',
        key: ''
      }
    ]
  }
  /** Upon creating new IChartModule, copy all the codes above - END */

  /** Loaded setting and generate elements */
  ui: {
    /** Generated from Charts.class */
    echart?: any
    noDataMsg: string
    noData?: boolean
  } = {
    noDataMsg: this._t.instant('No data is present'),
    noData: true
  }

  constructor(
    private _t: TranslateService
  ) { }

  ngOnInit(): void {
    this.initComp$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        this.generateChart()
      })
  }

  ngOnDestroy(): void {
    this.unsubber.next()
    this.unsubber.unsubscribe()
  }

  /**
   * Generates the chart based on data and mapping variable
   */
  generateChart() {
    const mapping = this._mapping
    const data = this._data
    const setting = this._settings

    if (!mapping || !data?.length) return

    const genIBasicOpt = (data, map: IMapTemplate, settings = this._settings) => {
      const fields = map.fields
      const { diffColorBars, hideAxisVal } = settings

      /** Default object. Should remain constant */
      const defObj: IBasicOpt = {
        visual: {
          xaxisname: '',
          yaxisname: '',
          isHorizontal: false,
          boundaryGap: true,
          categoryData: [],
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'none'
            }
          },
          borderRadius: 5
        },
        series: [{
          type: 'bar',
          data: []
        }]
      }
      const workObj: IBasicOpt = { ...defObj }

      const fieldsObj = fields.reduce((p, field) => {
        p[field.id] = field
        return p
      }, {} as any)

      const { xaxis, yaxis } = fieldsObj
      const custColor = fieldsObj['custom-color']
      const dataIds = fieldsObj['data-id']


      workObj.visual.xaxisname = xaxis.text
      workObj.visual.yaxisname = yaxis.text

      let catData = [], seriesData = [], customColors = [], ids = []

      data.map(ele => {
        catData.push(ele[xaxis.key])
        seriesData.push(ele[yaxis.key])
        customColors.push(ele[custColor.key])
        ids.push(ele[dataIds.key])
      })

      if (diffColorBars) {
        const unsorted = seriesData.reduce((p, ele, idx) => {
          if (!this._settings.showEmptyBars && !ele) return p

          
          if (this.ui.noData) this.ui.noData = !ele

          p.push({
            value: ele || 0,
            id: ids[idx],
            name: catData[idx],
            color: customColors[idx],
            itemStyle: { color: customColors[idx] }
          })

          return p
        }, [])
        workObj.series[0].data = sortBySeverityColors(unsorted, 'name', true)
      }
      else {
        workObj.series[0].data = seriesData.reduce((p, ele, idx) => {
          if (!this._settings.showEmptyBars && !ele) return p
          
          p.push({
            value: ele || 0,
            id: ids[idx],
            name: catData[idx]
          })

          return p
        }, [])
      }
      workObj.visual.categoryData = workObj.series[0].data.map(res => this._t.instant(res.name))

      if (hideAxisVal) {
        workObj.visual.xAxis = {
          show: false
        }
        workObj.visual.yAxis = {
          show: false
        }
        workObj.series[0].label = {
          show: true,
          position: 'top'
        }
        workObj.visual.grid = {
          width: '90%',
          bottom: '5%',
          left: '5%',
          top: '15%'
        }
      }

      return workObj
    }

    const basicOpt = genIBasicOpt(data, mapping)
    const chart = Charts.genLineBarOpt(basicOpt)

    this.ui.echart = chart
  }

  /** Emit out event when chart is clicked */
  onChartClicked(evt) {
    /**
     * TODO:
     * Emit the event together when refactoring for Charts Module
     */
    this.chartClicked.emit(evt)
  }
}
